import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { useTranslation } from 'react-i18next';
import LoadingCenter from '../util/LoadingCenter';
import EmptyState from '../util/EmptyState';
import MobileSearchFilter from './MobileSearchFilter';
import ProfileCard from '../modal/ProfileCard';
import SidePanel from '../util/SidePanel';

interface Props { access: any; baseUrl?: string; }

const asArray = (d: any) => (Array.isArray(d) ? d : d?.data ?? []);
const initials = (f?: string, l?: string) => `${(f || '').trim()[0] || ''}${(l || '').trim()[0] || ''}`.toUpperCase() || '—';
const ACTIVE_ABS = ['pending', 'approved', 'recorded'];

type Section = 'employees' | 'leave';

const MobileHr: React.FC<Props> = ({ access, baseUrl = '' }) => {
  const { t } = useTranslation();
  const auth = useSelector((state: RootState) => state.auth.value);
  const [section, setSection] = useState<Section>('employees');
  const [users, setUsers] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>(null);
  const [depts, setDepts] = useState<any[]>([]);
  const [absences, setAbsences] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [hrFilter, setHrFilter] = useState('all');

  // Annual Leave
  const [leaveRows, setLeaveRows] = useState<any[]>([]);
  const [leaveYear, setLeaveYear] = useState<number | null>(null);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [leaveLoaded, setLeaveLoaded] = useState(false);
  const [leaveSel, setLeaveSel] = useState<any>(null);

  const canViewLeave = access?.hr_leave_view_sv === true || access?.hr_leave_view_dep === true
    || access?.hr_leave_view_all === true || access?.hr_absence_view_all === true || access?.hr_absence_view_team === true;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, d, a] = await Promise.allSettled([
        axios.get('users'), axios.get('department'), axios.get('hr-absence'),
      ]);
      if (u.status === 'fulfilled') setUsers(asArray(u.value.data));
      if (d.status === 'fulfilled') setDepts(asArray(d.value.data));
      if (a.status === 'fulfilled') setAbsences(asArray(a.value.data));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { if (auth) load(); }, [auth, load]);

  const loadLeave = useCallback(async () => {
    setLeaveLoading(true);
    try {
      const q = leaveYear !== null ? `?year=${leaveYear}` : '';
      const { data } = await axios.get(`hr-absence/balances${q}`);
      setLeaveRows(Array.isArray(data?.rows) ? data.rows : []);
      if (leaveYear === null && data?.leaveYear) setLeaveYear(data.leaveYear);
      setLeaveLoaded(true);
    } catch {
      setLeaveRows([]);
      setLeaveLoaded(true);
    } finally {
      setLeaveLoading(false);
    }
  }, [leaveYear]);

  // ленивая загрузка балансов при первом открытии раздела + перезагрузка при смене года
  useEffect(() => {
    if (section === 'leave' && canViewLeave && (!leaveLoaded || leaveYear !== null)) loadLeave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, leaveYear]);

  const deptName = useCallback((id: any) => depts.find((d) => d.id === id)?.name || '', [depts]);

  const absentTodayIds = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const set = new Set<number>();
    absences.forEach((a) => {
      const s = (a.startDate || '').slice(0, 10);
      const e = (a.endDate || a.startDate || '').slice(0, 10);
      if (s && today >= s && today <= e && ACTIVE_ABS.includes(a.status)) set.add(a.userId);
    });
    return set;
  }, [absences]);

  const q = search.trim().toLowerCase();
  const absentUsers = useMemo(() => users.filter((u) => absentTodayIds.has(u.id)), [users, absentTodayIds]);
  const list = useMemo(() => {
    const base = hrFilter === 'absent' ? absentUsers : users;
    return base.filter((u) => !q || `${u.first_name ?? ''} ${u.last_name ?? ''} ${u.job_title ?? ''} ${deptName(u.departmentId)}`.toLowerCase().includes(q));
  }, [users, absentUsers, q, deptName, hrFilter]);

  // ── Leave helpers ──
  const unitOf = (b: any) => (b?.patternUnit === 'hours' ? 'hours' : 'days');
  const fmtVal = (b: any, days: any, hours: any) =>
    (days === null || days === undefined) ? '—'
      : unitOf(b) === 'hours' ? `${Math.round((hours ?? 0) * 10) / 10}${t('HShort')}` : `${days}${t('DShort')}`;
  const defBalance = (row: any) => {
    const bal = row.balances ?? [];
    return bal.find((b: any) => b.isDefault) ?? bal[0] ?? null;
  };

  const leaveList = useMemo(() => {
    if (!q) return leaveRows;
    return leaveRows.filter((r: any) => `${r.user?.first_name ?? ''} ${r.user?.last_name ?? ''} ${r.user?.job_title ?? ''}`.toLowerCase().includes(q));
  }, [leaveRows, q]);

  if (loading) return <LoadingCenter />;

  const Avatar = ({ u }: { u: any }) => (
    u?.avatarUrl
      ? <img className="m-av" src={`${baseUrl.replace(/\/$/, '')}${u.avatarUrl.startsWith('/') ? '' : '/'}${u.avatarUrl}`} alt="" />
      : <span className="m-av">{initials(u?.first_name, u?.last_name)}</span>
  );

  const SECTIONS: { value: Section; label: string }[] = [
    { value: 'employees', label: t('Employees') as string },
    ...(canViewLeave ? [{ value: 'leave' as Section, label: t('AnnualLeave') as string }] : []),
  ];

  return (
    <div className="flex flex-col gap-[10px]">
      {SECTIONS.length > 1 && (
        <div className="m-chips">
          {SECTIONS.map((s) => (
            <button
              type="button"
              key={s.value}
              className={`m-chip${section === s.value ? ' on' : ''}`}
              onClick={() => setSection(s.value)}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      {/* ═══ EMPLOYEES ═══ */}
      {section === 'employees' && (
        <>
          <MobileSearchFilter
            value={search}
            onChange={setSearch}
            options={[
              { value: 'all', label: t('All'), count: users.length },
              { value: 'absent', label: t('AbsentToday'), count: absentUsers.length },
            ]}
            filterValue={hrFilter}
            onFilter={setHrFilter}
          />

          {absentUsers.length > 0 && (
            <div className="m-dash-card">
              <div className="section-label" style={{ marginBottom: 10 }}>{t('AbsentToday')} · {absentUsers.length}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {absentUsers.slice(0, 8).map((u) => (
                  <span key={u.id} className="m-av" style={{ width: 34, height: 34, fontSize: 12, cursor: 'pointer' }} onClick={() => setSelected(u)}>{initials(u.first_name, u.last_name)}</span>
                ))}
              </div>
            </div>
          )}

          <div className="section-label" style={{ margin: '2px 2px' }}>{t('Employees')} · {users.length}</div>
          {list.length === 0 ? (
            <EmptyState icon="👥" message={t('NoData') as string} />
          ) : (
            <div className="m-list">
              {list.map((u) => {
                const absent = absentTodayIds.has(u.id);
                return (
                  <div key={u.id} className="m-li" onClick={() => setSelected(u)}>
                    <Avatar u={u} />
                    <span className="m-li-main">
                      <span className="m-li-title">{`${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || '—'}</span>
                      <span className="m-li-sub">{[u.job_title, deptName(u.departmentId)].filter(Boolean).join(' · ')}</span>
                    </span>
                    <span className={`m-dot ${absent ? 'off' : 'on'}`} title={absent ? (t('AbsentToday') as string) : ''} />
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ═══ ANNUAL LEAVE ═══ */}
      {section === 'leave' && (
        <>
          <MobileSearchFilter value={search} onChange={setSearch} />

          <div className="section-label" style={{ margin: '2px 2px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{t('AnnualLeave')} · {leaveRows.length}</span>
            {leaveYear != null && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button type="button" className="m-year-btn" onClick={() => setLeaveYear((y) => (y ?? new Date().getFullYear()) - 1)}>‹</button>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{leaveYear}</span>
                <button type="button" className="m-year-btn" onClick={() => setLeaveYear((y) => (y ?? new Date().getFullYear()) + 1)}>›</button>
              </span>
            )}
          </div>

          {leaveLoading ? (
            <LoadingCenter />
          ) : leaveList.length === 0 ? (
            <EmptyState icon="🏖️" message={t('NoData') as string} />
          ) : (
            <div className="m-list">
              {leaveList.map((r: any) => {
                const b = defBalance(r);
                const u = r.user || {};
                const ent = b?.entitlementDays;
                const used = b?.usedDays ?? 0;
                const low = b && b.remainingDays !== null && b.remainingDays < 0;
                const pct = ent && ent > 0 ? Math.min(100, Math.round((used / ent) * 100)) : 0;
                return (
                  <div key={u.id} className="m-li" onClick={() => setLeaveSel(r)}>
                    <Avatar u={u} />
                    <span className="m-li-main">
                      <span className="m-li-title">{`${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || '—'}</span>
                      <span className="m-li-sub">{t('HrUsed')} {fmtVal(b, used, b?.usedHours)} · {t('HrEntitlement')} {fmtVal(b, ent, b?.entitlementHours)}</span>
                      <div style={{ marginTop: 7, height: 6, borderRadius: 4, background: 'var(--bg-disable, rgba(148,163,184,.25))', overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: 4, width: `${pct}%`, background: low ? '#dc2626' : 'linear-gradient(90deg,#2563eb,#7c3aed)' }} />
                      </div>
                    </span>
                    <span className="m-li-right">
                      <span className="m-li-amt" style={{ color: b && b.remainingDays !== null ? (low ? '#dc2626' : '#059669') : 'var(--text-color)' }}>{fmtVal(b, b?.remainingDays, b?.remainingHours)}</span>
                      <div className="m-li-sub" style={{ marginTop: 2 }}>{t('HrRemaining')}</div>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── Профиль сотрудника — выезжает справа, как профиль из шапки ── */}
      <SidePanel open={!!selected} onClose={() => setSelected(null)} width={760}>
        {selected && (
          <ProfileCard
            selectedUser={selected}
            hrMode
            access={access}
            setShow={() => setSelected(null)}
            refreshUsers={load}
            refreshUserHeder={load}
            baseUrl={baseUrl}
          />
        )}
      </SidePanel>

      {/* ── Детали отпуска сотрудника ── */}
      {leaveSel && (
        <>
          <div className="m-sheet-scrim" onClick={() => setLeaveSel(null)} />
          <div className="m-sheet">
            <div className="m-sheet-grab" />
            <div className="m-sheet-head">
              <div>
                <div className="m-sheet-title">{`${leaveSel.user?.first_name ?? ''} ${leaveSel.user?.last_name ?? ''}`.trim() || '—'}</div>
                {leaveSel.user?.job_title && <div className="m-sheet-sub">{leaveSel.user.job_title}</div>}
              </div>
            </div>
            <div className="m-sheet-body">
              {(leaveSel.balances ?? []).length === 0 ? (
                <EmptyState icon="🏖️" message={t('NoData') as string} />
              ) : (
                (leaveSel.balances ?? []).map((b: any) => {
                  const ent = b.entitlementDays;
                  const used = b.usedDays ?? 0;
                  const low = b.remainingDays !== null && b.remainingDays < 0;
                  const pct = ent && ent > 0 ? Math.min(100, Math.round((used / ent) * 100)) : 0;
                  return (
                    <div key={b.typeId} className="m-dash-card" style={{ marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: b.typeColor || '#7c3aed', flexShrink: 0 }} />
                        <span style={{ fontWeight: 800, fontSize: 14 }}>{b.typeName}</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
                        <div>
                          <div className="m-li-sub">{t('HrEntitlement')}</div>
                          <div style={{ fontWeight: 800, fontSize: 15 }}>{fmtVal(b, ent, b.entitlementHours)}</div>
                        </div>
                        <div>
                          <div className="m-li-sub">{t('HrUsed')}</div>
                          <div style={{ fontWeight: 800, fontSize: 15 }}>{fmtVal(b, used, b.usedHours)}</div>
                        </div>
                        <div>
                          <div className="m-li-sub">{t('HrRemaining')}</div>
                          <div style={{ fontWeight: 800, fontSize: 15, color: b.remainingDays !== null ? (low ? '#dc2626' : '#059669') : 'var(--text-color)' }}>{fmtVal(b, b.remainingDays, b.remainingHours)}</div>
                        </div>
                      </div>
                      {(b.pendingDays ?? 0) > 0 && (
                        <div className="m-li-sub" style={{ marginBottom: 8 }}>{t('Pending')}: {fmtVal(b, b.pendingDays, b.pendingHours)}</div>
                      )}
                      <div style={{ height: 7, borderRadius: 4, background: 'var(--bg-disable, rgba(148,163,184,.25))', overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: 4, width: `${pct}%`, background: low ? '#dc2626' : 'linear-gradient(90deg,#2563eb,#7c3aed)' }} />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MobileHr;
